/**
 * The shapes `apps/api/src/readmodels/studio-clients-list.ts`,
 * `studio-client-detail.ts` and `studio-money.ts` answer with — session 17 (B).
 *
 * `.docs/00-index.md` §5: "Cross-module read models for a page". These three read
 * models compose every module a studio page reads from, and this file is their one
 * wire shape, so `apps/web` and the action registry both reach it through
 * `@fareast/shared` (`readmodels/index.ts`'s own convention).
 *
 * Every field that already has a home elsewhere is reused rather than restated:
 * `RetainerSchema`, `AllowanceStatusSchema`, `WorkOrderSchema`, `InvoiceSchema` /
 * `InvoiceDetailSchema`, `PaymentSchema`, `OpenDecisionSchema`, `BillingRunSchema`,
 * `UnmatchedPaymentSchema`, `MemberSchema`, `InvitationSchema` and
 * `ClientFilesSummarySchema` are all published shapes from the modules that own
 * them; this file only adds what is genuinely new to these three screens.
 */
import { z } from 'zod';
import { BillingRunSchema, InvoiceEstimateSchema, InvoiceSchema, PaymentRailSchema, PaymentSourceSchema, UnmatchedPaymentSchema, } from '../billing.js';
import { InvitationSchema, MarketTierSchema, MemberSchema } from '../clients.js';
import { ClientFilesSummarySchema } from '../files.js';
import { CurrencySchema, DateOnlySchema, MoneySchema, PeriodSchema, Rfc3339Schema, } from '../primitives.js';
import { ClientSlugSchema, TaskRefSchema } from '../refs.js';
import { AllowanceStatusSchema, RetainerSchema, RetainerStatusSchema } from '../retainers.js';
import { OpenDecisionSchema, WorkOrderSchema } from '../work-orders.js';
/* ------------------------------------------------------------- studio-clients-list */
export const STUDIO_CLIENTS_LIST_STATUSES = [
    'active',
    'past_due',
    'cancelled',
    'no_retainer',
    'all',
];
export const StudioClientsListStatusSchema = z
    .enum(STUDIO_CLIENTS_LIST_STATUSES)
    .describe('Which clients to include, by their retainer’s own status. "no_retainer" is every ' +
    'open account that has never had one — a project or one-off client, or one on widgets ' +
    'or prepaid usage alone. "all" is every client: each that has ever had a retainer, ' +
    'cancelled ones included, and each open account without one.');
export const StudioClientMrrSchema = z
    .object({
    native: MoneySchema.describe('The retainer’s own monthly amount, in its own currency.'),
    usd_eq: MoneySchema.describe('The same figure converted to USD (`pricing.usdEquivalent`) — equal to `native` for a ' +
        'USD client. What every studio-wide total, including concentration, sums.'),
})
    .describe('MRR, native and USD-equivalent — `clients.html`’s "€890 / $970".');
export const StudioClientRowSchema = z
    .object({
    client: ClientSlugSchema,
    name: z.string().describe('The company, as everybody says it out loud.'),
    since: DateOnlySchema.describe('When the retainer started — or, for a client that has never had one, the day the ' +
        'account was opened.'),
    package: z
        .string()
        .nullable()
        .describe('The package name, or "Custom — <name>" when it was built from blocks. Null for a ' +
        'client with no retainer.'),
    blocks: z.int().describe('How many blocks the retainer has. 0 with no retainer.'),
    tier: MarketTierSchema,
    currency: CurrencySchema,
    mrr: StudioClientMrrSchema.nullable().describe('Null once the retainer is cancelled, and for a client that has never had one.'),
    retainer_status: RetainerStatusSchema.nullable().describe('Null for a client that has never had a retainer — the "no_retainer" filter’s rows.'),
    retainer_note: z
        .string()
        .nullable()
        .describe('"work stopped 22 Aug", "at period end, 31 May" — null when there is none.'),
    allowance_burn_pct: z
        .number()
        .nullable()
        .describe('Used ÷ included across the retainer’s items, each weighted by its share of the ' +
        'package price (`formulae.md` §Allowance burn). **Not clamped** — over 1 is work ' +
        'past the allowance, which is the whole point of the column. Null when the ' +
        'retainer has no metered allowance at all.'),
    value_pct: z
        .number()
        .nullable()
        .describe('Value delivered ÷ paid, this month — the same ratio `analytics_client_health` ' +
        'answers, from the same computation. Null when nothing was paid.'),
    extras_unbilled: MoneySchema.nullable().describe('Extras agreed this period that no invoice has carried yet, in the client’s own ' +
        'currency — `studio-client-detail.ts`’s own ledger sum, reused so the two pages ' +
        'cannot disagree.'),
    under_scoped: z
        .boolean()
        .describe('> 130% of value for two consecutive months (`formulae.md` §% of value). One ' +
        'predicate, computed in `readmodels/client-month.ts` and read here and by ' +
        '`analytics_client_health`.'),
    effective_rate: MoneySchema.nullable().describe('$/hour of retainer work, **in USD** — `formulae.md` §Effective $/hour, the same ' +
        'figure `analytics_effective_rate.rate` answers. USD even for a EUR client, because ' +
        'a column headed `$/h` is a comparison. Null with no retainer hours.'),
    overdue: MoneySchema.nullable().describe('The outstanding remainder of any past-due invoice.'),
    overdue_note: z.string().nullable().describe('"ACH retry 26 Aug" — null when there is none.'),
    last_contact: Rfc3339Schema.nullable().describe('The latest of: a call, a comment, an approval, a portal sign-in. Null for nobody yet.'),
})
    .describe('One row of the studio clients list.');
export const STUDIO_DECISION_KINDS = [
    'quote',
    'extra',
    'package_change',
    'content_plan',
    'quote_due',
    'task',
];
export const StudioDecisionKindSchema = z
    .enum(STUDIO_DECISION_KINDS)
    .describe('What kind of decision it is. The four `OpenDecisionSchema` names, plus `quote_due` — a ' +
    'draft the studio owes a price for, past its own due date — and `task`, an open ' +
    'decision-tagged account or finance task.');
export const StudioDecisionWaitingOnSchema = z
    .enum(['client', 'studio'])
    .describe('Which side is holding it up. `studio` is the only one the studio can act on today.');
export const StudioClientsListDecisionSchema = z
    .object({
    client: ClientSlugSchema,
    kind: StudioDecisionKindSchema,
    ref: z.string().describe('The thing’s own reference — WO-2026-022, RET-CHG-004, TSK-0067.'),
    task_ref: TaskRefSchema.nullable().describe('Set only for `kind: task`, so the row can link to the board. Null otherwise.'),
    title: z.string().describe('One line — the quote’s title, the task’s own title.'),
    waiting_on: StudioDecisionWaitingOnSchema,
    since: Rfc3339Schema.nullable().describe('When it was proposed or raised. Null for a draft.'),
    due_on: DateOnlySchema.nullable().describe('The date it is against: a quote’s `valid_until`, or the day the studio owed a price.'),
})
    .describe('One line of "Waiting on a decision" — `formulae.md` §Waiting on a decision: every ' +
    'decision open on any account, whichever side is holding it up. Not only the ' +
    'decision-tagged tasks it was until an exploratory pass found the page announcing ' +
    '"No account has an open decision" over five open proposals.');
export const StudioClientsListConcentrationSchema = z
    .object({
    total_mrr: MoneySchema.describe('Every live client’s MRR, USD-equivalent, added up.'),
    top1_share: z.number().describe('The largest client’s MRR ÷ the total. 0.384 is "38.4%".'),
    top2_share: z.number().describe('The largest two clients’ MRR ÷ the total.'),
    bottom3_share: z.number().describe('The smallest three clients’ MRR ÷ the total.'),
    clients_to_lose_half: z
        .int()
        .describe('The smallest N whose MRR is at least half the total, ranked by size.'),
})
    .describe('Concentration over every live client’s MRR (formulae.md §Concentration).');
export const StudioClientsListSchema = z
    .object({
    filter: StudioClientsListStatusSchema,
    rows: z.array(StudioClientRowSchema).describe('One row per client, filtered by status.'),
    concentration: StudioClientsListConcentrationSchema,
    decisions: z
        .array(StudioClientsListDecisionSchema)
        .describe('The "waiting on a decision" list — what the studio owes first, then oldest ' +
        '(`formulae.md` §Waiting on a decision).'),
})
    .describe('`studio/clients.html`, whole: the book and the decisions list.');
/* ------------------------------------------------------------ studio-client-detail */
/**
 * Who the page is about — session 20.
 *
 * Four facts the client detail page prints before it prints anything else, and none
 * of which was on this shape: the title and the hero cap read the **slug**, "New work
 * order" could not know which tier to quote a client with no retainer at, and
 * `.docs/20` §Edge cases' "no Impersonate for archived" had nothing to test.
 *
 * `name` is `organization.name` — Better Auth's, and the one column `client` has no
 * copy of (`modules/clients/queries.ts` `StaffClientRow` explains why) — so it is a
 * join here as it is there rather than a second read at the call site.
 */
export const StudioClientAccountSchema = z
    .object({
    slug: ClientSlugSchema,
    name: z.string().describe('The company, as everybody says it out loud.'),
    tier: MarketTierSchema,
    currency: CurrencySchema.describe('What they are billed in, whatever a retainer says.'),
    archived_at: Rfc3339Schema.nullable().describe('When the account was archived, or null. An archived client is read-only: there is ' +
        'nothing to view as and nothing to quote.'),
})
    .describe('The account itself, before any of the money on it.');
export const StudioClientDetailHeroSchema = z
    .object({
    period: PeriodSchema,
    month_total: MoneySchema.describe('What this month bills — the retainer line plus every extra agreed this month, billed ' +
        'or not, in the client’s own currency (`formulae.md` §Paid).'),
    next_estimate: InvoiceEstimateSchema.nullable().describe('A dry run of the next billing period (`billing.estimateFor`). Null with nothing to bill.'),
    effective_rate: MoneySchema.nullable().describe('`month_total` ÷ retainer hours, **in USD** — `formulae.md` §Effective $/hour, the ' +
        'same figure `clients_summary` and `analytics_effective_rate` answer. USD even ' +
        'where `month_total` is EUR: a $/hour is a comparison across the book.'),
    effective_rate_hours: z.number().nullable().describe('The retainer hours behind the rate.'),
    rate_floor: MoneySchema.describe('`setting(rate_floor)` — the number the rate is read against.'),
})
    .describe('The hero: what the account is worth this month.');
export const StudioExtrasLedgerLineSchema = z
    .object({
    ref: z.string().describe('REQ-115, or the extra’s own work order reference.'),
    description: z.string().describe('The line as it reads in the ledger.'),
    approval_note: z
        .string()
        .nullable()
        .describe('"Deniz Aksoy · 4 Aug", or "rolling approval" for a standing-approval overage.'),
    amount: MoneySchema.describe('What it bills at — zero for a waived line.'),
    waived_amount: MoneySchema.nullable().describe('What it would have billed, when waived.'),
})
    .describe('One line of the extras ledger.');
export const StudioClientDetailExtrasLedgerSchema = z
    .object({
    total_unbilled: MoneySchema,
    lines: z
        .array(StudioExtrasLedgerLineSchema)
        .describe('Extras approved this period, in ref order.'),
})
    .describe('Extras approved this month, not yet billed — `billing.billableExtras`’ own rows.');
export const OPEN_ITEM_KINDS = ['blocked_on_client', 'ours_to_quote'];
export const StudioOpenItemSchema = z
    .object({
    kind: z
        .enum(OPEN_ITEM_KINDS)
        .describe('blocked_on_client — a task waiting on them. ours_to_quote — a draft we owe a price.'),
    task_ref: TaskRefSchema.nullable().describe('Set for a blocked task; null for a quote we owe.'),
    work_order_ref: z.string().nullable().describe('Set for a quote we owe; null for a task.'),
    title: z.string().describe('What it is, one line.'),
    note: z.string().describe('The sub-line — who is waiting, since when, and on what.'),
    /*
     * The two fields session 24 (R2-30) split out of `note`.
     *
     * The quote due date used to be interpolated into `note` as a raw `YYYY-MM-DD`,
     * so the one date on that panel was the only one on the page not going through
     * `useFormat()` — and being a sentence rather than a value, nothing could tell
     * that it was four days past. Both are read-model facts, not renderings: the day
     * itself, and how late it is **in the studio's own zone**, which is the zone
     * `openItemsFor` already decides overdue-ness in and the one the browser does not
     * know.
     *
     * Optional on the wire, and only for as long as `apps/web/src/test/world/*.json`
     * has not been re-recorded — `pnpm world:record` fills them. A recording taken
     * before a field existed is still a valid answer, and a page that falls back to
     * `note` renders exactly what it rendered before.
     */
    due_on: DateOnlySchema.nullable()
        .default(null)
        .describe('The day a quote we owe was due. Null for a task blocked on the client.'),
    days_late: z
        .int()
        .nullable()
        .default(null)
        .describe('Calendar days past due_on, in the studio’s zone. Never zero or negative: an item ' +
        'that is not late yet is not an open item at all. Null when due_on is.'),
})
    .describe('One line of "Open items": tasks waiting_on the client, and our overdue quotes.');
export const StudioClientDetailContactsSchema = z
    .object({
    members: z.array(MemberSchema).describe('Everybody holding a seat, owners first.'),
    invitations: z.array(InvitationSchema).describe('Invitations still pending.'),
    seats_used: z.int().describe('Members plus pending invitations.'),
    seat_limit: z.int().describe('How many seats the account may hold.'),
})
    .describe('Who is on the account.');
export const StudioAdSpendBandSchema = z
    .object({
    spent: MoneySchema,
    band: MoneySchema,
    note: z.string().describe('"band decision due 29 Aug" — the meter’s own sub-line.'),
})
    .describe('The ad-spend meter, from this period’s report `ads` section. Null when there is none.');
export const StudioActivityItemSchema = z
    .object({
    at: Rfc3339Schema,
    kind: z.string().describe('The action name that wrote the row — reads like the tool list.'),
    title: z
        .string()
        .describe('One line, built from the audit row: what it was about by its own reference, and ' +
        'what happened to it — "PLAN-2026-09 — updated item". The kind of thing stands in ' +
        'for the reference when the entity could not be named.'),
    /**
     * Session 24 (R2-34). The panel named nobody, on the one surface whose whole job
     * is who did what.
     *
     * `.default(null)` because this field is newer than the recorded world
     * (`apps/web/src/test/world/studio.json`); a payload taken before it parses as
     * "we cannot say", which is what an older recording honestly means.
     */
    actor: z
        .string()
        .nullable()
        .default(null)
        .describe('Who did it — a studio member’s name, an agent’s own name, or "the portal" for a ' +
        'job nobody asked for. Null when the actor’s row has gone.'),
    note: z.string().describe('A second line of detail, when there is one. Often empty.'),
})
    .describe('One line of the last-30-days activity timeline.');
/**
 * What was folded into a project's `total_amount`, so the studio can reconcile the
 * two figures one project has.
 *
 * `readmodels/studio-client-detail.ts` `withScopeChangesFolded` adds every billable
 * scope change to its parent's total, because `client-detail.html` prints
 * `WO-2026-008` at **$5,050.00** — the four milestones' $4,730 plus `REQ-117`'s $320
 * — with "1 scope change attached" as the sub-line. The figure landed and the
 * sub-line did not, so the studio's own two screens read $5,050.00 and "Agreed total
 * $4,730.00" with nothing anywhere naming the $320 between them, while the client's
 * Projects page reconciles the same pair in a sentence.
 *
 * The fold **overwrites** a field whose own description says it is the row's own
 * agreed total, and that is what makes it unreadable rather than merely unexplained:
 * without these three numbers a reader cannot recover either the quote or the delta
 * from the row. So the fold now says what it did.
 *
 * Nothing here is a second computation of anything: `agreed_total` is the row's
 * `total_amount` before folding, `total` is the sum of the children
 * `isBillableScopeChange` accepted, and `total_amount` is their sum. The page adds
 * nothing up.
 */
export const StudioScopeChangeFoldSchema = z
    .object({
    count: z
        .int()
        .describe('How many billable scope changes were added in. At least one when this is set.'),
    total: MoneySchema.describe('What they come to — the difference this fold made.'),
    agreed_total: MoneySchema.describe('The project’s own agreed total before the fold: what its milestones add up to, and ' +
        'what /studio/work-orders/<ref> prints as “Agreed total”.'),
})
    .describe('The scope changes folded into one project’s displayed total.');
/**
 * A work order on the studio's client detail: the published row, plus what the fold
 * above did to it.
 *
 * **`.optional()` as well as `.nullable()`, and the two mean different things.** The
 * read model always writes the key — `null` for a row with nothing folded, which is
 * every row but a project carrying a scope change — so `null` is the server's answer
 * and the SPA renders it as "nothing to reconcile". *Absent* is a payload written
 * before this field existed, which is every row of the committed recording
 * (`apps/web/src/test/world/studio.json`) until `pnpm world:record` is run again.
 *
 * `StudioActivityItemSchema.actor` took `.default(null)` for the same situation, and
 * that is not enough on its own: the SPA reads a route's answer through the RPC types
 * and never parses it with zod, so a default fills the field in `world.test.ts` and
 * leaves it `undefined` on the page. A reader has to handle both, and the type is what
 * makes it do so.
 */
export const StudioWorkOrderRowSchema = WorkOrderSchema.extend({
    scope_changes: StudioScopeChangeFoldSchema.nullable()
        .optional()
        .describe('Null when nothing was folded, which is every row but a project with a scope change ' +
        'against it. Absent only from a payload recorded before this field existed.'),
}).describe('One work order as the studio’s client detail lists it.');
/**
 * Money booked to this account as income, with no invoice — session 47.
 *
 * Far East Novels' Patreon payouts are the reason: they arrive by Wise through Payoneer
 * and settle nothing, so the invoices table cannot show them, and before this block the
 * account's page showed nothing at all about money the studio had received for it.
 */
export const StudioClientIncomeSchema = z
    .object({
    sender_names: z
        .array(z.string())
        .describe('The names this client’s Wise transfers arrive under (clients_set_wise_senders).'),
    credits_are_income: z
        .boolean()
        .describe('Whether a credit from one of those names is booked here as income by itself.'),
    payments: z
        .array(z.object({
        external_id: z.string().describe('The bank’s id for the credit — wise:TRANSFER-….'),
        received_at: Rfc3339Schema.describe('When the money arrived.'),
        amount: MoneySchema,
        source: PaymentSourceSchema,
        sender: z.string().describe('The name it arrived under; empty when none was given.'),
        booked_by: z
            .enum(['rule', 'person'])
            .describe('Booked by the sender rule as it arrived, or by a person from the queue.'),
        note: z.string().describe('What the person who booked it wrote. Empty for the rule.'),
    }))
        .describe('The latest income booked to this account, newest first — at most twelve.'),
})
    .describe('Income booked to this account with no invoice (session 47).');
export const StudioClientDetailSchema = z
    .object({
    client: ClientSlugSchema,
    account: StudioClientAccountSchema,
    period: PeriodSchema,
    hero: StudioClientDetailHeroSchema,
    extras_ledger: StudioClientDetailExtrasLedgerSchema,
    open_items: z
        .array(StudioOpenItemSchema)
        .describe('What is open with the client, and what the studio owes them.'),
    decisions: z
        .array(OpenDecisionSchema)
        .describe('Quotes, extras, package changes and content-plan sign-offs still open.'),
    retainer: RetainerSchema.nullable().describe('The live retainer, or null with none.'),
    allowances: z
        .array(AllowanceStatusSchema)
        .describe('This period’s allowance status, per item.'),
    ad_spend: StudioAdSpendBandSchema.nullable(),
    work_orders: z
        .array(StudioWorkOrderRowSchema)
        .describe('Projects and extras, most recent first, each scope change folded into its own.'),
    invoices: z.array(InvoiceSchema).describe('This client’s invoices, most recent first.'),
    income: StudioClientIncomeSchema,
    contacts: StudioClientDetailContactsSchema,
    files: ClientFilesSummarySchema,
    activity: z
        .array(StudioActivityItemSchema)
        .describe('The last 30 days of portal activity, newest first.'),
})
    .describe('`studio/client-detail.html`, whole.');
/* ------------------------------------------------------------------- studio-money */
export const MoneyRunRowSchema = z
    .object({
    client: ClientSlugSchema,
    invoice_ref: z
        .string()
        .nullable()
        .describe('The invoice the run made, or null with nothing to bill.'),
    retainer: MoneySchema.nullable(),
    extras: MoneySchema.nullable(),
    usage: MoneySchema.nullable(),
    total: MoneySchema.nullable(),
    rail: PaymentRailSchema.nullable(),
    charge_state: z
        .string()
        .nullable()
        .describe('The run’s `charge` step state, or null when the run has not reached it.'),
})
    .describe('One row of the billing run table for a period.');
export const AgeingBandSchema = z
    .object({
    bucket: z.string().describe('"1–30 d", "31–60 d", "61–90 d" or "90+ d" (PAST_DUE_BUCKETS).'),
    amount: MoneySchema,
    refs: z.array(z.string()).describe('"casaturca FES-2026-047", one per invoice in the band.'),
})
    .describe('One ageing band, one currency.');
export const FailedPaymentSchema = z
    .object({
    invoice_ref: z.string().describe('The invoice the charge was against.'),
    client: ClientSlugSchema,
    total: MoneySchema,
    rail: PaymentRailSchema.nullable(),
    code: z
        .string()
        .nullable()
        .describe('The gateway’s own failure code — "card_declined", "R01".'),
    attempt: z.int().describe('Which attempt failed, counting from 1.'),
    of: z.int().describe('How many attempts this rail (and code) allows.'),
    next_retry_at: Rfc3339Schema.nullable().describe('Null once the schedule has run out.'),
    failed_at: Rfc3339Schema.describe('When the newest failed charge against it came back.'),
    /**
     * Whether a later attempt worked, and whether the schedule has run out.
     *
     * **Two booleans rather than a sentence to parse.** The read model computes both
     * to write `action_hint`; publishing only the sentence made the SPA recover the
     * first half with `action_hint.toLowerCase().startsWith('recovered')` and restate
     * the second term for term. That is not cosmetic — the page picks a chase mail's
     * default wording from `exhausted`, so a drift mails a client "we are not going to
     * try again automatically" while the retry schedule still would.
     *
     * They are deliberately not one enum: a charge can be neither, and the three
     * non-recovered outcomes `action_hint` distinguishes are what the table prints.
     */
    recovered: z.boolean().describe('True when a later attempt settled the invoice.'),
    exhausted: z
        .boolean()
        .describe('True only when the schedule has genuinely run out — not recovered, nothing ' +
        'scheduled, and every attempt this rail allows already made. A charge with ' +
        'attempts left and nothing scheduled is one invoices_retry_charge$ away.'),
    action_hint: z.string().describe('"next 26 Aug" or "no further attempt — call them".'),
})
    .describe('One row of the failed-payments table.');
export const RecognisedCollectedMonthSchema = z
    .object({
    period: PeriodSchema,
    recognised: MoneySchema,
    collected: MoneySchema,
})
    .describe('One month of recognised revenue against what was actually collected.');
export const MoneyLedgerRowSchema = z
    .object({
    ref: z.string().describe('The invoice’s own reference — FES-2026-047.'),
    client: ClientSlugSchema,
    scope: z.string().describe('"Aug 2026", "Website — milestone 2" — the invoice’s own scope.'),
    issued_at: Rfc3339Schema.nullable(),
    total: MoneySchema,
    status: z.string().describe('draft, sent, processing, paid or failed.'),
})
    .describe('One line of the invoice ledger fold — a compact view, not the full invoice.');
export const StudioMoneySchema = z
    .object({
    period: PeriodSchema,
    run: z.array(MoneyRunRowSchema).describe('One row per client billed this period.'),
    runs: z.array(BillingRunSchema).describe('The run metadata itself, one per client billed.'),
    invoice_ledger: z
        .array(MoneyLedgerRowSchema)
        .describe('The most recent invoices, across every client.'),
    failed_payments: z
        .array(FailedPaymentSchema)
        .describe('Charges that failed, most recent first.'),
    ageing: z
        .array(AgeingBandSchema)
        .describe('Past-due totals by PAST_DUE_BUCKETS band, in order.'),
    wise_queue: z
        .array(UnmatchedPaymentSchema)
        .describe('Wise credits waiting on a person to place them.'),
    recognised_vs_collected: z
        .array(RecognisedCollectedMonthSchema)
        .describe('The last six months.'),
})
    .describe('`studio/money.html`’s "Money in — run", the ledger, failed payments and the Wise queue.');
//# sourceMappingURL=studio.js.map